import {Controller} from '@hotwired/stimulus';

const COOKIE_MAX_KEYS = 50;
const COOKIE_MAX_AGE = 365 * 24 * 3600;

/**
 * Intro d'écran (`page_help()`, `Lang::help()`) : « Masquer l'aide » la retire, et
 * elle reste masquée dans ce navigateur pour cet utilisateur. Sans `localStorage` (navigation privée,
 * stockage bloqué), le masquage ne vaut que pour la page affichée.
 *
 * Un autre encart masquable reprend le contrôleur avec sa propre entrée de `localStorage` : `storageKey` en donne le
 * nom complet (à la place de `key` et `user`), `mark` la valeur qui le masque.
 *
 *     <div {{ stimulus_controller('page-help', {storageKey: 'booking-stop-message-' ~ siteId, mark: 'hide'}) }}>
 *         <a href="#" {{ stimulus_action('page-help', 'hide:prevent') }}>…</a>
 *     </div>
 *
 * Rendu par le serveur puis retiré à la connexion du contrôleur, un encart déjà fermé apparaît le temps du
 * chargement du JS. `cookie` nomme un cookie où l'encart fermé inscrit aussi sa `storageKey`, pour que le serveur ne
 * le rende plus ; un encart fermé avant l'existence du cookie s'y inscrit à sa prochaine connexion.
 */
export default class extends Controller {
  static values = {key: String, user: String, storageKey: String, mark: {type: String, default: '1'}, cookie: String};

  connect() {
    if (this.read() === this.markValue) {
      this.rememberInCookie();
      this.element.remove();
    }
  }

  hide() {
    try {
      localStorage.setItem(this.storageKey, this.markValue);
    } catch {
      // le masquage ne survivra pas au rechargement
    }

    this.rememberInCookie();
    this.element.remove();
  }

  rememberInCookie() {
    if (!this.hasCookieValue) {
      return;
    }

    const keys = this.cookieKeys().filter((key) => key !== this.storageKey);
    keys.push(this.storageKey);

    const value = encodeURIComponent(keys.slice(-COOKIE_MAX_KEYS).join(','));
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';

    document.cookie = `${this.cookieValue}=${value}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  }

  cookieKeys() {
    const prefix = `${this.cookieValue}=`;
    const entry = document.cookie.split('; ').find((cookie) => cookie.startsWith(prefix));

    if (!entry) {
      return [];
    }

    try {
      return decodeURIComponent(entry.slice(prefix.length)).split(',').filter((key) => key !== '');
    } catch {
      return [];
    }
  }

  read() {
    try {
      return localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }

  get storageKey() {
    if (this.hasStorageKeyValue) {
      return this.storageKeyValue;
    }

    return `page-help-hidden:${this.userValue}:${this.keyValue}`;
  }
}
